import { useEffect, useMemo, useRef } from 'react';
import { useDownloadExcel } from 'react-export-table-to-excel';
import { useDispatch, useSelector } from 'react-redux';
import { clearAppointmentConfirmationPercentageByBranch } from '../../../../appRedux/actions/Reports';
import { appointmentConfirmationPercentageReportByBranchConst } from '../../../../constants';
import { formatFileName } from '../../../../util/strings';
import { ExportButton } from '../../../uiComponents/ExportButton';
import { ReportTableHeader } from '../../../uiComponents/ReportTableHeader';
import { AppointmentConfirmationPercentageByBranchFilter } from '../../ReportFilter/AppointmentConfirmationPercentageByBranchFilter';
import { DataNotFound } from '../DataNotFound';

export const AppointmentConfirmationPercentageReportByBranch = () => {
  const tableRef = useRef(null);
  const dispatch = useDispatch();
  const { appointmentConfirmationPercentageByBranch } = useSelector((state) => state.reports);
  const { fullname, role } = useSelector((state) => state.userAuth);
  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: formatFileName(appointmentConfirmationPercentageReportByBranchConst.title),
    sheet: appointmentConfirmationPercentageReportByBranchConst.title,
  });
  const hasData = !!Object.keys(appointmentConfirmationPercentageByBranch).length;
  const header = appointmentConfirmationPercentageReportByBranchConst.header;
  const ranges = appointmentConfirmationPercentageReportByBranchConst.colorRanges || {};
  const getColor = (pct) => {
    const match = Object.values(ranges).find((r) => pct >= r.min && pct <= r.max);
    return match?.color || ranges?.high?.color || '#9FE47D';
  };

  const { rows, totals, globalPercentage } = useMemo(() => {
    const source = appointmentConfirmationPercentageByBranch || [];
    const rows = source.map((r) => {
      const total =
        Number(r.yes) +
        Number(r.no) +
        Number(r.voicemail) +
        Number(r.whatsapp) +
        Number(r.email) +
        Number(r.app) +
        Number(r.ecommerce);

      const percentage = total > 0
        ? Math.min(Number((((Number(r.confirmed) || 0) / total) * 100).toFixed(2)), 100)
        : 0;

      return { ...r, total, percentage, color: getColor(percentage) };
    });

    const totals = rows.reduce(
      (acc, r) => {
        acc.fullYes += Number(r.yes) || 0;
        acc.fullNo += Number(r.no) || 0;
        acc.fullVoicemail += Number(r.voicemail) || 0;
        acc.fullWhatsapp += Number(r.whatsapp) || 0;
        acc.fullEmail += Number(r.email) || 0;
        acc.fullApp += Number(r.app) || 0;
        acc.fullEcommerce += Number(r.ecommerce) || 0;
        acc.fullConfirmed += Number(r.confirmed) || 0;
        acc.fullTotal += Number(r.total) || 0;
        return acc;
      },
      {
        fullYes: 0,
        fullNo: 0,
        fullVoicemail: 0,
        fullWhatsapp: 0,
        fullEmail: 0,
        fullApp: 0,
        fullEcommerce: 0,
        fullConfirmed: 0,
        fullTotal: 0,
      }
    );

    const globalPercentage = totals.fullTotal > 0
      ? Number(((totals.fullConfirmed / totals.fullTotal) * 100).toFixed(2))
      : 0;

    return { rows, totals, globalPercentage };
  }, [appointmentConfirmationPercentageByBranch, ranges]);

  const columns = [
    { key: 'branch', colSpan: 2, align: 'center', style: { padding: '15px' } },
    { key: 'yes', colSpan: 2, align: 'center' },
    { key: 'no', colSpan: 2, align: 'center' },
    { key: 'voicemail', colSpan: 2, align: 'center' },
    { key: 'whatsapp', colSpan: 2, align: 'center' },
    { key: 'email', colSpan: 2, align: 'center' },
    { key: 'app', colSpan: 2, align: 'center' },
    { key: 'ecommerce', colSpan: 2, align: 'center' },
    { key: 'total', colSpan: 2, align: 'center' },
    {
      key: 'percentage',
      colSpan: 2,
      align: 'center',
      dynamicStyle: (row) => ({ backgroundColor: row.color }),
      render: (row) => <b>{row.percentage}%</b>,
    },
  ];

  useEffect(() => {
    dispatch(clearAppointmentConfirmationPercentageByBranch());
  }, [dispatch]);

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <ExportButton onDownload={onDownload} disabled={!hasData} />
        <AppointmentConfirmationPercentageByBranchFilter />
      </div>

      <div ref={tableRef}>
        {hasData ? (
          <table style={{ margin: '0 auto' }}>
            <ReportTableHeader
              user={fullname}
              role={role}
              title={appointmentConfirmationPercentageReportByBranchConst.title}
              col1={10}
              col2={0}
              col3={14}
            />
            <tbody>
              <tr>
                {header?.map((item, key) => (
                  <th
                    key={key}
                    colSpan={2}
                    style={{ border: '1px solid black', padding: '15px' }}
                  >
                    {item}
                  </th>
                ))}
              </tr>
              {rows.map((row, key) => (
                <tr key={key}>
                  {columns.map((col) => {
                    const baseStyle = {
                      border: '1px solid black',
                      textAlign: col.align || 'left',
                    };
                    const extraStyle = typeof col.dynamicStyle === 'function' ? col.dynamicStyle(row) : {};
                    const fixedStyle = col.style || {};
                    return (
                      <td key={col.key} colSpan={col.colSpan} style={{ ...baseStyle, ...fixedStyle, ...extraStyle }}>
                        {col.render ? col.render(row) : row[col.key]}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                    padding: '15px',
                  }}
                >
                  <h4>{appointmentConfirmationPercentageReportByBranchConst.labels.totalAppointments}</h4>
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                  }}
                >
                  {totals.fullYes}
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                  }}
                >
                  {totals.fullNo}
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                  }}
                >
                  {totals.fullVoicemail}
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                  }}
                >
                  {totals.fullWhatsapp}
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                  }}
                >
                  {totals.fullEmail}
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                  }}
                >
                  {totals.fullApp}
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                  }}
                >
                  {totals.fullEcommerce}
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                  }}
                >
                  {totals.fullTotal}
                </td>
                <td
                  colSpan={2}
                  style={{
                    border: '1px solid black',
                    textAlign: 'center',
                    backgroundColor: '#e6e9eb',
                  }}
                >
                  <b>{globalPercentage}%</b>
                </td>
              </tr>
              <tr>
                <td
                  colSpan={20}
                  style={{
                    backgroundColor: ranges?.high?.color,
                    textAlign: 'center',
                    padding: '10px',
                  }}
                >
                  <b>{appointmentConfirmationPercentageReportByBranchConst.legend.high}</b>
                </td>
              </tr>
              <tr>
                <td
                  colSpan={20}
                  style={{
                    backgroundColor: ranges?.medium?.color,
                    textAlign: 'center',
                    padding: '10px',
                  }}
                >
                  <b>{appointmentConfirmationPercentageReportByBranchConst.legend.medium}</b>
                </td>
              </tr>
              <tr>
                <td
                  colSpan={20}
                  style={{
                    backgroundColor: ranges?.low?.color,
                    textAlign: 'center',
                    padding: '10px',
                  }}
                >
                  <b>{appointmentConfirmationPercentageReportByBranchConst.legend.low}</b>
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <DataNotFound />
        )}
      </div>
    </div>
  );
};
